import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import { AuthContext } from '../context/AuthContext';
import { SERVER_URL } from '../config';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/CalendarStyles.css'; // Import our custom calendar styles
import { FaCalendarAlt, FaClock, FaUser, FaEnvelope, FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
import { FaFileDownload, FaClosedCaptioning } from 'react-icons/fa';

// Setup the localizer for BigCalendar
const localizer = momentLocalizer(moment);

const Calendar = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add this line to fix the ESLint errors
  const [meetingTranscript, setMeetingTranscript] = useState(null);
  const [showTranscriptTab, setShowTranscriptTab] = useState(false);
  const [createdMeetings, setCreatedMeetings] = useState([]);
  const [attendedMeetings, setAttendedMeetings] = useState([]);
  const [invitedMeetings, setInvitedMeetings] = useState([]);
  const [missedMeetings, setMissedMeetings] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'created', 'attended', 'invited', 'missed'
  
  // Form state for creating/editing a meeting
  const [formData, setFormData] = useState({
    title: '',
    scheduledTime: new Date(),
    duration: 60,
    participants: []
  });
  
  // Participant form state
  const [participantEmail, setParticipantEmail] = useState('');
  const [participantUsername, setParticipantUsername] = useState('');
  
  useEffect(() => {
    if (user) {
      fetchMeetings();
    }
  }, [user]);
  
  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${SERVER_URL}/scheduled-meetings/user/${user.username}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch meetings');
      }
      
      const data = await response.json();
      
      // Transform meetings for calendar display
      const formattedMeetings = data.meetings.map(meeting => ({
        id: meeting.meetingId,
        title: meeting.title,
        start: new Date(meeting.scheduledTime),
        end: new Date(new Date(meeting.scheduledTime).getTime() + meeting.duration * 60000),
        hostUsername: meeting.hostUsername,
        participants: meeting.participants,
        status: meeting.status,
        originalMeeting: meeting
      }));
      
      // Categorize meetings
      const now = new Date();
      const created = [];
      const attended = [];
      const invited = [];
      const missed = [];
      
      formattedMeetings.forEach(meeting => {
        // Meetings created by the user
        if (meeting.hostUsername === user.username) {
          created.push(meeting);
        }
        
        // Meetings where user is a participant
        const isParticipant = meeting.participants.some(p => 
          p.username === user.username || p.email === user.email
        );
        
        if (isParticipant) {
          // Check if meeting is in the past and status is not 'completed'
          if (meeting.end < now && meeting.status !== 'completed') {
            missed.push(meeting);
          } else {
            invited.push(meeting);
          }
        }
        
        // For attended meetings, we would need a record of attendance
        // This would typically come from the backend
        // For now, we'll assume meetings with status 'completed' were attended
        if (isParticipant && meeting.status === 'completed') {
          attended.push(meeting);
          // Remove from missed if it was marked as attended
          const missedIndex = missed.findIndex(m => m.id === meeting.id);
          if (missedIndex !== -1) {
            missed.splice(missedIndex, 1);
          }
        }
      });
      
      setMeetings(formattedMeetings);
      setCreatedMeetings(created);
      setAttendedMeetings(attended);
      setInvitedMeetings(invited);
      setMissedMeetings(missed);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectSlot = ({ start }) => {
    // Open the meeting creation modal with the selected time
    setFormData({
      ...formData,
      scheduledTime: start,
      participants: []
    });
    setShowMeetingModal(true);
  };
  
  const fetchMeetingTranscript = async (meetingId) => {
    try {
      const response = await fetch(`${SERVER_URL}/transcriptions/meeting/${meetingId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMeetingTranscript(data);
      } else {
        setMeetingTranscript(null);
      }
    } catch (error) {
      console.error('Error fetching meeting transcript:', error);
      setMeetingTranscript(null);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedMeeting(event);
    setShowDetailsModal(true);
    setShowTranscriptTab(false); // Reset to default tab
    
    // Fetch transcript for this meeting
    fetchMeetingTranscript(event.id);
  };
  
  const downloadTranscript = (meetingId) => {
    window.open(`${SERVER_URL}/transcriptions/download/${meetingId}?format=txt`, '_blank');
  };
  
  const handleCreateMeeting = async () => {
    try {
      if (!formData.title) {
        alert('Please enter a meeting title');
        return;
      }
      
      // Add a flag to prevent multiple submissions
      if (isSubmitting) return;
      setIsSubmitting(true);
      
      const meetingData = {
        title: formData.title,
        hostUsername: user.username,
        scheduledTime: formData.scheduledTime,
        duration: formData.duration,
        participants: formData.participants
      };
      
      const response = await fetch(`${SERVER_URL}/scheduled-meetings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(meetingData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create meeting');
      }
      
      const data = await response.json();
      
      // Close the modal and refresh meetings
      setShowMeetingModal(false);
      fetchMeetings();
      
      // Reset form data
      setFormData({
        title: '',
        scheduledTime: new Date(),
        duration: 60,
        participants: []
      });
      
      alert('Meeting scheduled successfully!');
    } catch (error) {
      console.error('Error creating meeting:', error);
      alert('Failed to schedule meeting. Please try again.');
    } finally {
      // Reset the submission flag
      setIsSubmitting(false);
    }
  };
  
  const handleAddParticipant = (e) => {
    if (e) e.preventDefault(); // Add this line to prevent form submission
    
    if (!participantEmail) {
      alert('Please enter participant email');
      return;
    }
    
    // Add participant to the form data
    setFormData({
      ...formData,
      participants: [
        ...formData.participants,
        {
          email: participantEmail,
          username: participantUsername || participantEmail.split('@')[0]
        }
      ]
    });
    
    // Clear participant form
    setParticipantEmail('');
    setParticipantUsername('');
  };
  
  const handleRemoveParticipant = (index) => {
    const updatedParticipants = [...formData.participants];
    updatedParticipants.splice(index, 1);
    
    setFormData({
      ...formData,
      participants: updatedParticipants
    });
  };
  
  const handleJoinMeeting = async (meetingId) => {
    // Check if the current user is the host of the selected meeting
    if (selectedMeeting && selectedMeeting.hostUsername === user.username) {
      try {
        // Host is starting the meeting - create a new stream
        const response = await fetch(`${SERVER_URL}/streams`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            title: selectedMeeting.title,
            hostUsername: user.username,
            streamId: meetingId // Use the meeting ID as the stream ID
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to start meeting');
        }
        
        const data = await response.json();
        
        // Navigate to the stream page as host
        navigate(`/stream/${data.streamId}`, {
          state: {
            streamId: data.streamId,
            title: data.title
          }
        });
      } catch (error) {
        console.error('Error starting meeting:', error);
        alert('Failed to start meeting. Please try again.');
      }
    } else {
      // Non-host participants join as viewers
      navigate(`/view/${meetingId}`, { state: { username: user.username } });
    }
  };
  
  const handleEditMeeting = (meeting) => {
    setFormData({
      title: meeting.title,
      scheduledTime: new Date(meeting.start),
      duration: (meeting.end - meeting.start) / 60000,
      participants: meeting.participants || []
    });
    
    setSelectedMeeting(meeting);
    setShowDetailsModal(false);
    setShowMeetingModal(true);
  };
  
  const handleUpdateMeeting = async () => {
    try {
      if (!selectedMeeting) return;
      
      // Add a flag to prevent multiple submissions
      if (isSubmitting) return;
      setIsSubmitting(true);
      
      const meetingData = {
        title: formData.title,
        scheduledTime: formData.scheduledTime,
        duration: formData.duration,
        participants: formData.participants
      };
      
      // Use originalMeeting.meetingId instead of id for the API endpoint
      const response = await fetch(`${SERVER_URL}/scheduled-meetings/${selectedMeeting.originalMeeting.meetingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(meetingData)
      });
      
      if (!response.ok) {
        // Get the error message from the response
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update meeting');
      }
      
      // Close the modal and refresh meetings
      setShowMeetingModal(false);
      setSelectedMeeting(null);
      fetchMeetings();
      
      // Reset form data
      setFormData({
        title: '',
        scheduledTime: new Date(),
        duration: 60,
        participants: []
      });
      
      alert('Meeting updated successfully!');
    } catch (error) {
      console.error('Error updating meeting:', error);
      alert(`Failed to update meeting: ${error.message}`);
    } finally {
      // Reset the submission flag
      setIsSubmitting(false);
    }
  };
  
  const handleCancelMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) return;
    
    try {
      const response = await fetch(`${SERVER_URL}/scheduled-meetings/${meetingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        // Get the error message from the response
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel meeting');
      }
      
      // Close the modal and refresh meetings
      setShowDetailsModal(false);
      setSelectedMeeting(null);
      fetchMeetings();
      
      alert('Meeting cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling meeting:', error);
      alert(`Failed to cancel meeting: ${error.message}`);
    }
  };
  
  return (
    <CalendarContainer>
      <CalendarHeader>
        <h1>Meeting Calendar</h1>
        <ScheduleButton onClick={(e) => {
          e.preventDefault(); // Add this line to prevent multiple submissions
          setSelectedMeeting(null);
          setFormData({
            title: '',
            scheduledTime: new Date(),
            duration: 60,
            participants: []
          });
          setShowMeetingModal(true);
        }}>
          Schedule New Meeting
        </ScheduleButton>
      </CalendarHeader>
      
      {loading ? (
        <LoadingMessage>Loading calendar...</LoadingMessage>
      ) : (
        <BigCalendarWrapper>
          <BigCalendar
            localizer={localizer}
            events={meetings}
            startAccessor="start"
            endAccessor="end"
            selectable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            style={{ height: 600 }}
            views={['month', 'week', 'day', 'agenda']}
            eventPropGetter={(event) => {
              let backgroundColor = '#4DABF7'; // Default light blue
              let textColor = 'white';
              
              if (event.status === 'cancelled') {
                backgroundColor = '#999';
              } else if (event.hostUsername === user.username) {
                backgroundColor = '#74C0FC'; // Lighter blue for host's events
              }
              
              // Create a title attribute for the tooltip
              const title = `${event.title}\n${moment(event.start).format('h:mm A')} - ${moment(event.end).format('h:mm A')}`;
              
              return { 
                className: 'calendar-event',
                style: { 
                  backgroundColor,
                  color: textColor,
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  padding: '4px 8px',
                },
                title: title // This will be used for the tooltip on hover
              };
            }}
          />
        </BigCalendarWrapper>
      )}
      
      {/* Meeting Creation/Edit Modal */}
      {showMeetingModal && (
        <ModalOverlay>
          <Modal>
            <ModalHeader>
              <h3>{selectedMeeting ? 'Edit Meeting' : 'Schedule New Meeting'}</h3>
              <CloseButton onClick={() => {
                setShowMeetingModal(false);
                setSelectedMeeting(null);
              }}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <ModalContent>
              <FormGroup>
                <Label>Meeting Title</Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter meeting title"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Date & Time</Label>
                <DatePickerWrapper>
                  <DatePicker
                    selected={formData.scheduledTime}
                    onChange={(date) => setFormData({ ...formData, scheduledTime: date })}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    minDate={new Date()}
                  />
                </DatePickerWrapper>
              </FormGroup>
              
              <FormGroup>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                  min="15"
                  step="15"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Add Participants</Label>
                <ParticipantInputGroup>
                  <Input
                    type="email"
                    value={participantEmail}
                    onChange={(e) => setParticipantEmail(e.target.value)}
                    placeholder="Participant email"
                  />
                  <Input
                    type="text"
                    value={participantUsername}
                    onChange={(e) => setParticipantUsername(e.target.value)}
                    placeholder="Participant name (optional)"
                  />
                  <AddButton onClick={(e) => handleAddParticipant(e)}>Add</AddButton>
                </ParticipantInputGroup>
              </FormGroup>
              
              {formData.participants.length > 0 && (
                <ParticipantsList>
                  <h4>Participants</h4>
                  {formData.participants.map((participant, index) => (
                    <ParticipantItem key={index}>
                      <ParticipantInfo>
                        <span>{participant.username || participant.email.split('@')[0]}</span>
                        <small>{participant.email}</small>
                      </ParticipantInfo>
                      <RemoveButton onClick={() => handleRemoveParticipant(index)}>
                        <FaTimes />
                      </RemoveButton>
                    </ParticipantItem>
                  ))}
                </ParticipantsList>
              )}
              
              <ButtonGroup>
                <CancelButton onClick={(e) => {
                  e.preventDefault();
                  setShowMeetingModal(false);
                  setSelectedMeeting(null);
                }}>
                  Cancel
                </CancelButton>
                <SubmitButton onClick={(e) => {
                  e.preventDefault();
                  if (selectedMeeting) {
                    handleUpdateMeeting();
                  } else {
                    handleCreateMeeting();
                  }
                }}>
                  {selectedMeeting ? 'Update Meeting' : 'Schedule Meeting'}
                </SubmitButton>
              </ButtonGroup>
            </ModalContent>
          </Modal>
        </ModalOverlay>
      )}
      
      {/* Meeting Details Modal */}
      {showDetailsModal && selectedMeeting && (
        <ModalOverlay>
          <Modal>
            <ModalHeader>
              <h3>Meeting Details</h3>
              <CloseButton onClick={() => setShowDetailsModal(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <ModalContent>
              <MeetingDetails>
                <MeetingTitle>{selectedMeeting.title}</MeetingTitle>
                
                <MeetingInfo>
                  <MeetingInfoItem>
                    <FaCalendarAlt />
                    <span>{moment(selectedMeeting.start).format('MMMM D, YYYY')}</span>
                  </MeetingInfoItem>
                  
                  <MeetingInfoItem>
                    <FaClock />
                    <span>
                      {moment(selectedMeeting.start).format('h:mm A')} - 
                      {moment(selectedMeeting.end).format('h:mm A')}
                    </span>
                  </MeetingInfoItem>
                  
                  <MeetingInfoItem>
                    <FaUser />
                    <span>Host: {selectedMeeting.hostUsername}</span>
                  </MeetingInfoItem>
                  
                  {selectedMeeting.status === 'cancelled' && (
                    <MeetingStatus>Cancelled</MeetingStatus>
                  )}
                </MeetingInfo>
                
                {selectedMeeting.participants && selectedMeeting.participants.length > 0 && (
                  <ParticipantsList>
                    <h4>Participants</h4>
                    {selectedMeeting.participants.map((participant, index) => (
                      <ParticipantItem key={index}>
                        <ParticipantInfo>
                          <span>{participant.username || participant.email.split('@')[0]}</span>
                          <small>{participant.email}</small>
                        </ParticipantInfo>
                      </ParticipantItem>
                    ))}
                  </ParticipantsList>
                )}
                
                <ButtonGroup>
                  <MeetingDetailTabs>
                  <MeetingDetailTab 
                    active={!showTranscriptTab} 
                    onClick={() => setShowTranscriptTab(false)}
                  >
                    Details
                  </MeetingDetailTab>
                  <MeetingDetailTab 
                    active={showTranscriptTab} 
                    onClick={() => setShowTranscriptTab(true)}
                    disabled={!meetingTranscript}
                  >
                    <FaClosedCaptioning /> Transcript
                  </MeetingDetailTab>
                </MeetingDetailTabs>

                {!showTranscriptTab ? (
                  // Existing meeting details content
                  <>
                    {selectedMeeting.status !== 'cancelled' && (
                      <>
                        {new Date() >= new Date(selectedMeeting.start) && (
                          <JoinButton onClick={() => handleJoinMeeting(selectedMeeting.id)}>
                            Join Meeting
                          </JoinButton>
                        )}
                        
                        {selectedMeeting.hostUsername === user.username && (
                          <>
                            <EditButton onClick={() => handleEditMeeting(selectedMeeting)}>
                              <FaEdit /> Edit
                            </EditButton>
                            
                            <DeleteButton onClick={() => handleCancelMeeting(selectedMeeting.id)}>
                              <FaTrash /> Cancel Meeting
                            </DeleteButton>
                          </>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  // Transcript tab content
                  <TranscriptContainer>
                    {meetingTranscript ? (
                      <>
                        <TranscriptHeader>
                          <h4>Meeting Transcript</h4>
                          <DownloadButton onClick={() => downloadTranscript(selectedMeeting.id)}>
                            <FaFileDownload /> Download
                          </DownloadButton>
                        </TranscriptHeader>
                        <TranscriptContent>
                          {meetingTranscript.transcript.length > 0 ? (
                            meetingTranscript.transcript.map((segment, index) => (
                              <TranscriptSegment key={index}>
                                <TranscriptSpeaker>{segment.speakerUsername}</TranscriptSpeaker>
                                <TranscriptTime>
                                  {new Date(segment.timestamp).toLocaleTimeString()}
                                </TranscriptTime>
                                <TranscriptText>{segment.text}</TranscriptText>
                              </TranscriptSegment>
                            ))
                          ) : (
                            <EmptyTranscript>No transcript content available</EmptyTranscript>
                          )}
                        </TranscriptContent>
                      </>
                    ) : (
                      <EmptyTranscript>No transcript available for this meeting</EmptyTranscript>
                    )}
                  </TranscriptContainer>
                )}
                </ButtonGroup>
              </MeetingDetails>
            </ModalContent>
          </Modal>
        </ModalOverlay>
      )}
    </CalendarContainer>
  );
};

// Styled Components
const CalendarContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h1 {
    margin: 0;
    color: var(--text-primary);
  }
`;

const ScheduleButton = styled.button`
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

const BigCalendarWrapper = styled.div`
  background: white;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  padding: 20px;
  
  .rbc-calendar {
    font-family: 'Inter', sans-serif;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 18px;
  color: var(--text-secondary);
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  
  h3 {
    margin: 0;
    color: var(--text-primary);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const ModalContent = styled.div`
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }
  
  .react-datepicker__input-container input {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 16px;
  }
`;

const ParticipantInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 10px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AddButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  
  &:hover {
    background: var(--primary-dark);
  }
`;

const ParticipantsList = styled.div`
  margin-bottom: 20px;
  
  h4 {
    margin-top: 0;
    margin-bottom: 10px;
    color: var(--text-primary);
  }
`;

const ParticipantItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const ParticipantInfo = styled.div`
  display: flex;
  flex-direction: column;
  
  span {
    font-weight: 500;
  }
  
  small {
    color: #666;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ff5252;
  cursor: pointer;
  font-size: 16px;
  
  &:hover {
    color: #ff0000;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  background: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  
  &:hover {
    background: #e5e5e5;
  }
`;

const SubmitButton = styled.button`
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`;

const MeetingDetails = styled.div`
  padding: 10px 0;
`;

const MeetingTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--text-primary);
`;

const MeetingInfo = styled.div`
  margin-bottom: 20px;
`;

const MeetingInfoItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  
  svg {
    margin-right: 10px;
    color: var(--primary);
  }
`;

const MeetingStatus = styled.div`
  display: inline-block;
  background: #ff5252;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-weight: 500;
  margin-top: 10px;
`;

const JoinButton = styled.button`
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`;

const EditButton = styled.button`
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    background: #3367d6;
  }
`;

const DeleteButton = styled.button`
  background: #ff5252;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    background: #ff0000;
  }
`;

const MeetingDetailTabs = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
`;

const MeetingDetailTab = styled.button`
  padding: 0.5rem 1rem;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  border-bottom: 2px solid ${props => props.active ? 'var(--primary)' : 'transparent'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: ${props => props.disabled ? '0.5' : '1'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover {
    color: var(--primary);
  }
`;

const TranscriptContainer = styled.div`
  padding: 0.5rem;
`;

const TranscriptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h4 {
    margin: 0;
  }
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background: var(--primary-dark);
  }
`;

const TranscriptContent = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: var(--radius-sm);
`;

const TranscriptSegment = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background: white;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-xs);
`;

const TranscriptSpeaker = styled.div`
  font-weight: 600;
  color: var(--primary);
  font-size: 0.9rem;
`;

const TranscriptTime = styled.div`
  font-size: 0.7rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
`;

const TranscriptText = styled.div`
  font-size: 0.9rem;
`;

const EmptyTranscript = styled.div`
  text-align: center;
  color: var(--text-secondary);
  padding: 2rem 0;
  font-style: italic;
`;

export default Calendar;